import * as React from "react";
import {
  getAuth,
  fetchSignInMethodsForEmail,
  linkWithPopup,
  unlink,
  GithubAuthProvider,
  OAuthProvider,
  GoogleAuthProvider,
} from "firebase/auth";
import { Button, Divider, Input, notification, Tag } from "antd";
import { getCancelToken, getLocalToken, request } from "../../services/cpln";
import { CancelTokenSource } from "axios";
import { NavLink, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { SyntaxHighlight } from "../../components/syntaxHighlight/SyntaxHighlight";
import { TimezoneDisplay } from "../../components/layout/header/TimezoneDisplay";
import { IS_DEPLOYMENT_ENV_TEST } from "../../envVariables";
import { useAppSelector } from "../../store/hooks";
import { FormLabel } from "../../components/antd/form/FormLabel";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { FormItemDescription } from "../../components/antd/form/FormItemDescription";
import { selectOrg } from "../../store/contextSlice";
import { getFirebaseApp } from "../../store/firebase";
import { selectIsCplnTeam } from "../../store/userSlice";
import { sentryLog } from "../../tools/sentry";

const githubProvider = new GithubAuthProvider();
const microsoftProvider = new OAuthProvider("microsoft.com").setCustomParameters({
  prompt: "select_account",
});
const googleProvider = new GoogleAuthProvider().setCustomParameters({ prompt: "select_account" });

const providers = [
  {
    name: "Google",
    id: "google.com",
    firebaseProvider: googleProvider,
    isLinked: function (data: string[]) {
      return data.includes(this.id);
    },
    imagePath: "/resources/logos/login/google.svg",
  },
  {
    name: "Microsoft",
    id: "microsoft.com",
    firebaseProvider: microsoftProvider,
    isLinked: function (data: string[]) {
      return data.includes(this.id);
    },
    imagePath: "/resources/logos/login/microsoft.svg",
  },
  {
    name: "Github",
    id: "github.com",
    firebaseProvider: githubProvider,
    isLinked: function (data: string[]) {
      return data.includes(this.id);
    },
    imagePath: "/resources/logos/login/github.svg",
  },
];

export const FirebaseUserProfile: React.FC<{}> = () => {
  const org = useAppSelector(selectOrg);
  const userEmail = useAppSelector((state) => state.user.email);
  const isCplnTeam = useAppSelector(selectIsCplnTeam);
  const theme = useAppSelector((state) => state.theme.theme);

  const navigate = useNavigate();
  const [tempOrg, setTempOrg] = React.useState(org || "");
  const [isLoadingMyGroups, setIsLoadingMyGroups] = React.useState(false);

  const [isLoading, setIsLoading] = React.useState(false);
  const [isLinking, setIsLinking] = React.useState("");
  const [isUnlinking, setIsUnlinking] = React.useState("");
  const [data, setData] = React.useState<string[]>(null as any);
  const [triggerCount, setTriggerCount] = React.useState(0);

  const [localToken, setLocalToken] = React.useState("");
  const [showToken, setShowToken] = React.useState(false);

  const [sentryMessage, setSentryMessage] = React.useState("");
  const [sentryLogContent, setSentryLogContent] = React.useState("");

  React.useEffect(() => {
    setData(null as any);
    const user = getAuth(getFirebaseApp()).currentUser;
    const email = user?.email;
    if (email) {
      fetchSignInMethodsForEmail(getAuth(getFirebaseApp()), email).then((res: any) => {
        setData(res);
      });
    }
  }, [triggerCount]);

  React.useEffect(() => {
    if (!tempOrg) {
      setMyGroups([]);
      return;
    }
    fetchMyGroups();
  }, [tempOrg]);

  const myGroupsCancelTokenRef = React.useRef<CancelTokenSource | undefined>(undefined);
  const [myGroups, setMyGroups] = React.useState<string[]>([]);

  async function fetchMyGroups() {
    try {
      setIsLoadingMyGroups(true);
      if (myGroupsCancelTokenRef.current) {
        myGroupsCancelTokenRef.current.cancel("Cancelled");
      }
      myGroupsCancelTokenRef.current = getCancelToken();
      const cancelToken = myGroupsCancelTokenRef.current.token;
      const { data } = await request({ url: `/org/${tempOrg}/user/-myGroups`, cancelToken });
      setMyGroups(data.items);
      setIsLoadingMyGroups(false);
    } catch (e) {
      setIsLoadingMyGroups(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed to Fetch Groups",
        description: errorMessage,
      });
      setMyGroups([]);
    }
  }

  React.useEffect(() => {
    getLocalToken().then(({ accessToken }) => {
      setLocalToken(accessToken);
    });
  }, []);

  function getToken() {
    if (!localToken) {
      return {};
    }
    try {
      const decodedToken = jwtDecode(localToken);
      return decodedToken;
    } catch (e) {
      console.error("Failed to get token", e.message);
      return {};
    }
  }

  return (
    <div style={{ maxWidth: 900 }}>
      {IS_DEPLOYMENT_ENV_TEST ? (
        <div className="mb-12">
          <FormLabel>Timezone</FormLabel>
          <TimezoneDisplay hideLabel />
        </div>
      ) : null}
      <div className="mb-12">
        <Divider orientation={`left`} orientationMargin={0}>
          User Account Info
        </Divider>
        <div className="my-2">
          <FormLabel>Email</FormLabel>
          <div>{userEmail}</div>
        </div>
      </div>
      <div className="mb-12">
        <Divider orientation={`left`} orientationMargin={0}>
          Group Memberships
        </Divider>
        {/* TODO can be a kind select */}
        <div className="mb-4">
          <FormLabel>Org</FormLabel>
          <Input value={tempOrg} onChange={(e) => setTempOrg(e.target.value)} placeholder={`Type an org`} />
        </div>
        {tempOrg && !isLoadingMyGroups ? (
          <div className="mt-2">
            {myGroups.length < 1 ? (
              <Tag>{`No Group Memberships for Org '${tempOrg}'`}</Tag>
            ) : (
              <div className="border border-b-0" style={{ borderRadius: 6 }}>
                <div className="px-2 py-1 table-labels" style={{ borderTopLeftRadius: 6, borderTopRightRadius: 6 }}>
                  Group Name
                </div>
                {myGroups.map((g, index) => {
                  const lastItem = index === myGroups.length - 1;
                  return (
                    <div
                      key={g}
                      className={`px-2 py-1 border-b`}
                      style={{
                        borderBottomLeftRadius: lastItem ? 6 : undefined,
                        borderBottomRightRadius: lastItem ? 6 : undefined,
                      }}
                    >
                      <NavLink className="text-link" to={`/console${g}`}>
                        {g.split("/group/")[1]}
                      </NavLink>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}
      </div>
      <div className="mb-12">
        <Divider orientation={`left`} orientationMargin={0}>
          <div
            onClick={() => setShowToken((x) => !x)}
            className={`flex items-center gap-2 color-link-hover cursor-pointer`}
          >
            <span>Decoded JWT Token</span>
            <span>{showToken ? <UpOutlined className="h-4" /> : <DownOutlined className="h-4" />}</span>
          </div>
        </Divider>
        <div>
          {showToken ? (
            Object.keys(getToken()).length < 1 ? (
              "None"
            ) : (
              <SyntaxHighlight
                language="json"
                code={JSON.stringify(getToken(), null, 2)}
              />
            )
          ) : null}
        </div>
      </div>
      <div className="mb-12">
        <Divider orientation={`left`} orientationMargin={0}>
          Account Providers
        </Divider>
        {data === null ? (
          <div>Loading</div>
        ) : data.includes("password") ? (
          <div>Unavailable for email/password accounts</div>
        ) : (
          <div className="flex flex-col gap-2">
            {providers.map((p) => (
              <div key={p.name} className="flex items-center">
                <div className="flex items-center" style={{ width: "100%" }}>
                  {p.id === "github.com" && theme === "light" ? (
                    <img
                      className="mr-2"
                      style={{ width: 25, height: 25, backgroundColor: "black", padding: 2, borderRadius: "100%" }}
                      src={p.imagePath}
                    />
                  ) : (
                    <img className="mr-2" style={{ width: 25, height: 25 }} src={p.imagePath} />
                  )}
                  <div>{p.name}</div>
                </div>
                {p.isLinked(data) ? (
                  <Button
                    disabled={isLoading || data.length === 1}
                    loading={isUnlinking === p.id}
                    style={{ width: 100 }}
                    onClick={async () => {
                      if (data.length === 1) {
                        return;
                      }
                      setIsLoading(true);
                      setIsUnlinking(p.id);
                      try {
                        const user = getAuth(getFirebaseApp()).currentUser;
                        if (user) {
                          await unlink(user, p.id);
                        }
                        notification.success({ message: "Unlinked provider" });
                      } catch (e: any) {
                        notification.warning({ message: "Failed to unlink provider", description: e.message });
                      } finally {
                        setTriggerCount((x) => x + 1);
                        setIsUnlinking("");
                        setIsLoading(false);
                      }
                    }}
                    type={"default"}
                    danger
                  >
                    Unlink
                  </Button>
                ) : (
                  <Button
                    disabled={isLoading}
                    loading={isLinking === p.id}
                    style={{ width: 100 }}
                    onClick={async () => {
                      try {
                        setIsLoading(true);
                        setIsLinking(p.id);
                        const userToLink = getAuth(getFirebaseApp()).currentUser;
                        if (userToLink) {
                          await linkWithPopup(userToLink, p.firebaseProvider);
                          notification.success({ message: "Linked provider" });
                        } else {
                          throw new Error("User not found by firebase, try again.");
                        }
                      } catch (e: any) {
                        notification.warning({ message: "Failed to link provider", description: e.message });
                      } finally {
                        setTriggerCount((x) => x + 1);
                        setIsLinking("");
                        setIsLoading(false);
                      }
                    }}
                    type={"primary"}
                  >
                    Link
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {isCplnTeam ? (
        <div className="mb-12">
          <Divider orientation={`left`} orientationMargin={0}>
            Debug
          </Divider>
          <FormItemDescription>Only visible to CPLN Team</FormItemDescription>
          <FormLabel>Test Sentry</FormLabel>
          <div className="flex flex-col gap-2" style={{ width: 300 }}>
            <Input
              placeholder="Error message"
              value={sentryMessage}
              onChange={(e) => setSentryMessage(e.target.value)}
            />
            <Button
              type="default"
              onClick={() => {
                navigate(`/trigger-sentry?message=${sentryMessage}`);
              }}
            >
              Trigger Exception
            </Button>
          </div>
          <div className="flex flex-col gap-2" style={{ width: 300 }}>
            <Input
              placeholder="Log Content"
              value={sentryLogContent}
              onChange={(e) => setSentryLogContent(e.target.value)}
            />
            <Button
              type="default"
              onClick={() => {
                sentryLog("testlog", { "data.message": sentryLogContent });
                setSentryLogContent("");
              }}
            >
              Trigger Log
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
